import { State } from '@meraki-internal/state';
import { WithLinks } from '../caremap/WithLinks';
import { ICaremapNote, ICaremapNoteResponse, NotesClient } from '../notes/model/NotesClient';
import { AlertPresenter } from '../AlertBinder';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { IResourceChangedSocketMessage } from '../web-sockets/SocketState';
import { nanoid } from 'nanoid';
import { Logger } from '../support/Logger';
import { CareMapNoteTracker } from '../notes/model/CareMapNoteTracker';

export class CommentsModelFactory extends State<Record<string, never>> {
    static inject = () => [
        NotesClient,
        AlertPresenter,
        BufferedEventBus,
        Logger,
        CareMapNoteTracker
    ];
    constructor(
        public notesClient: NotesClient,
        public alert: AlertPresenter,
        public events: BufferedEventBus,
        public logger: Logger,
        public tracker: CareMapNoteTracker
    ) {
        super({});

        // events allows a limited number of subscribers
        // so subscribe once here and pass the message on
        this.events.on('web-sockets.message', msg => {
            for (const model of Object.values(this.noteIdToModel)) {
                model.onWebsocketMessage(msg as IResourceChangedSocketMessage);
            }
        });
    }

    private noteIdToModel: { [noteId: string]: CommentsModel } = {};

    forNote = (note: ICaremapNote): CommentsModel => {
        if (!this.noteIdToModel[note.noteId!]) {
            const model = new CommentsModel(note, this);

            model.load().catch(err => {
                this.alert.showAndLogError(err);
            });

            // if the model changes, we change, that way components can just subscrive to the factory
            // (likely called in a render, so avoid changing state synchronously)
            setTimeout(() => {
                model.subscribe(() => {
                    this.setState({});
                });
            });

            this.noteIdToModel[note.noteId!] = model;
        }
        return this.noteIdToModel[note.noteId!];
    };
}

export class CommentsModel extends State<Record<string, never>> {
    constructor(
        private note: ICaremapNote,
        private factory: CommentsModelFactory
    ) {
        super({});
    };

    private comments: ICaremapNoteResponse[] = [];

    private editingComment?: ICaremapNoteResponse;

    hasLoaded = false;

    onWebsocketMessage = async (message: IResourceChangedSocketMessage) => {
        if (message.resourceToRefresh === `${this.note.links?.self.href}/comments`) {
            try {
                await this.load();
                this.setState({ });
            } catch (err: any) {
                this.factory.logger.error(`CommentsModel.onWebsocketMessage failed updating comments for noteId ${this.note.noteId}. Inner error: ${err.toString()}`);
            };
        }
    };

    load = async () => {
        this.comments = await this.factory.notesClient.getNotes(this.note.links.comments);
        this.hasLoaded = true;
        this.setState({});
    };

    getComments = (): ICaremapNoteResponse[] => {
        return this.comments;
    };

    canAdd = () => {
        return (this.note.links.comments?.actions || []).includes('post-comment');
    };

    addNewComment = async ({ html }: { html: string }) => {
        const response = await this.factory.notesClient.addNote({
            parentLink: this.note.links.comments,
            note: {
                noteId: nanoid(),
                title: '',
                html,
                attachments: [],
                otherAttributes: {
                    noteTarget: { section: 'patient' },
                    parentNoteId: this.note.noteId
                }
            }
        });

        this.factory.tracker.onAddedNote(response, 'Community Comment Added');

        // when running locally with bypass, comment will already have been added via websocket
        // so only add it here if not already in the list
        if (!this.comments.some(c => c.noteId === response.noteId)) {
            this.comments.push(response);
            this.setState({});
        }

        return response;
    };

    canDelete = (comment: WithLinks<ICaremapNote>) => {
        return (comment.links.self?.actions || []).includes('delete-note');
    };

    deleteComment = async (comment: WithLinks<ICaremapNote>) => {
        const deletedNote = this.comments.find(c=>c.noteId === comment.noteId);
        await this.factory.notesClient.deleteNote(comment);

        if (deletedNote) {
            this.factory.tracker.onUpdatedNote(deletedNote, 'Community Comment Deleted');
        }

        this.comments = this.comments.filter(c => c.noteId !== comment.noteId);
        this.setState({});
    };

    canEdit = (comment: WithLinks<ICaremapNote>) => {
        return (comment.links.self?.actions || []).includes('put-note');
    };

    startEditing = (comment: any) => {
        this.editingComment = comment;
        this.setState({});
    };

    doneEditing = () => {
        this.editingComment = undefined;
        this.setState({});
    };

    getEditingComment = () => {
        return this.editingComment;
    };

    saveEditing = async (comment: ICaremapNoteResponse) => {
        const response = await this.factory.notesClient.updateNote(comment);

        this.factory.tracker.onUpdatedNote(response, 'Community Comment Edited');
        this.comments = [
            ...this.comments.filter(c => c.noteId !== comment.noteId),
            response
        ];

        this.doneEditing();
    };
}
